<template>
  <div class="topic-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">话题管理</h3>
        </div>
      </div>
      <button
        v-if="hasPerm(PERMISSIONS.CREATE_TOPIC)"
        @click="handleAdd"
        class="flex items-center gap-1.5 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        新增话题
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">这些话题出现在前台「问一问」页面的个人高频痛点 TOP 10、公司热点话题 TOP 10 以及搜索结果页</p>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-2">
          <span class="text-xs text-slate-400">类型：</span>
          <button
            v-for="t in typeFilterOptions"
            :key="t.value"
            @click="typeFilter = t.value"
            :class="[
              'px-3 py-1 text-xs rounded-full border transition-all',
              typeFilter === t.value
                ? 'bg-[#F2C94C] text-white border-[#F2C94C] font-medium'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'
            ]"
          >
            {{ t.label }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-slate-400">状态：</span>
          <button
            v-for="t in statusFilterOptions"
            :key="t.value"
            @click="statusFilter = t.value"
            :class="[
              'px-3 py-1 text-xs rounded-full border transition-all',
              statusFilter === t.value
                ? 'bg-[#F2C94C] text-white border-[#F2C94C] font-medium'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'
            ]"
          >
            {{ t.label }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-slate-400">标记：</span>
          <button
            v-for="t in tagFilterOptions"
            :key="t.value"
            @click="tagFilter = t.value"
            :class="[
              'px-3 py-1 text-xs rounded-full border transition-all',
              tagFilter === t.value
                ? 'bg-[#F2C94C] text-white border-[#F2C94C] font-medium'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'
            ]"
          >
            {{ t.label }}
          </button>
        </div>
        <div class="relative ml-auto">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索标题..."
            class="w-48 px-4 py-1.5 pl-9 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
          />
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
    </div>

    <!-- 话题列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredTopics" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="标题" min-width="200">
          <template #default="{ row }">
            <span class="text-sm font-medium text-slate-900">{{ row.title }}</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.type === '战友最痛' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'
              ]"
            >
              {{ row.type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="发布状态" width="100" align="center">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.publishStatus === 'published'
                  ? 'bg-emerald-50 text-emerald-600'
                  : row.publishStatus === 'draft'
                    ? 'bg-slate-100 text-slate-500'
                    : 'bg-red-50 text-red-500'
              ]"
            >
              {{ statusLabel(row.publishStatus) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="权重" width="80" align="center">
          <template #default="{ row }">
            <span class="text-sm text-slate-600">{{ row.sortWeight ?? 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="热门" width="80" align="center">
          <template #default="{ row }">
            <span v-if="row.isHot" class="inline-flex items-center px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded font-medium">HOT</span>
            <span v-else class="text-slate-300">-</span>
          </template>
        </el-table-column>
        <el-table-column label="Top10" width="80" align="center">
          <template #default="{ row }">
            <span v-if="row.isTop10" class="inline-flex items-center px-2 py-0.5 bg-[#F2C94C]/10 text-[#F2C94C] text-xs rounded font-medium">TOP10</span>
            <span v-else class="text-slate-300">-</span>
          </template>
        </el-table-column>
        <el-table-column label="关联话题" width="100" align="center">
          <template #default="{ row }">
            {{ row.relatedIds?.length || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button
                v-if="hasPerm(PERMISSIONS.EDIT_TOPIC)"
                @click="handleEdit(row)"
                class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors"
              >编辑</button>
              <button
                v-if="hasPerm(PERMISSIONS.DELETE_TOPIC)"
                @click="handleDelete(row)"
                class="text-red-400 hover:text-red-500 text-sm font-medium transition-colors"
              >删除</button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="filteredTopics.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        <div class="text-slate-400 text-sm">暂无话题</div>
      </div>
    </div>

    <!-- 编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-tabs v-model="activeTab">
        <!-- 基础信息 -->
        <el-tab-pane label="基础信息" name="basic">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">基础信息</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <el-form :model="form" label-width="90px" size="small">
              <el-form-item label="标题" required>
                <el-input v-model="form.title" placeholder="请输入话题标题" />
              </el-form-item>
              <el-form-item label="类型" required>
                <el-radio-group v-model="form.type">
                  <el-radio-button label="战友最痛">战友最痛</el-radio-button>
                  <el-radio-button label="组织必修">组织必修</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="发布状态">
                <el-radio-group v-model="form.publishStatus">
                  <el-radio-button label="draft">草稿</el-radio-button>
                  <el-radio-button label="published">已发布</el-radio-button>
                  <el-radio-button label="offline">已下线</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="AI解析">
                <el-input
                  v-model="form.caseStudy"
                  type="textarea"
                  :rows="8"
                  placeholder="请输入话题的AI智能解析内容..."
                />
              </el-form-item>
            </el-form>
          </div>
        </el-tab-pane>

        <!-- 关联配置 -->
        <el-tab-pane label="关联配置" name="related">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">关联话题</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <el-form :model="form" label-width="90px" size="small">
              <el-form-item label="关联话题">
                <el-select v-model="form.relatedIds" multiple placeholder="请选择关联话题" style="width: 100%">
                  <el-option
                    v-for="t in otherTopics"
                    :key="t.id"
                    :label="t.title"
                    :value="t.id"
                  />
                </el-select>
              </el-form-item>
            </el-form>

            <div class="text-xs text-slate-400 mt-2">
              关联后，前台用户浏览该话题时会看到相关推荐
            </div>
          </div>
        </el-tab-pane>

        <!-- 运营标记 -->
        <el-tab-pane
          v-if="hasPerm(PERMISSIONS.CONFIG_TOPIC)"
          label="运营标记"
          name="ops"
        >
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 mt-2 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="bg-amber-50 text-amber-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>修改运营标记将记录审计日志，请填写操作理由</span>
            </div>

            <el-form :model="form" label-width="100px" size="small">
              <el-form-item label="Top10">
                <el-switch v-model="form.isTop10" />
              </el-form-item>
              <el-form-item label="热门">
                <el-switch v-model="form.isHot" />
              </el-form-item>
              <el-form-item label="排序权重">
                <el-input-number v-model="form.sortWeight" :min="0" :max="100" controls-position="right" />
                <div class="text-xs text-slate-400 mt-1">数值越大排序越靠前，默认 0</div>
              </el-form-item>
              <el-form-item label="操作理由" required>
                <el-input
                  v-model="auditReason"
                  type="textarea"
                  :rows="2"
                  placeholder="请说明修改理由，将记录到审计日志"
                />
              </el-form-item>
            </el-form>
          </div>
        </el-tab-pane>
      </el-tabs>

      <span slot="footer">
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors mr-2"
        >
          取消
        </button>
        <button
          @click="handleSave"
          class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          保存
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { TOPICS } from '../../data'
import { PERMISSIONS } from '../../data/adminConfig'

const DEFAULT_FORM = {
  id: '',
  title: '',
  type: '战友最痛',
  publishStatus: 'draft',
  isTop10: false,
  isHot: false,
  sortWeight: 0,
  relatedIds: [],
  caseStudy: '',
}

export default {
  name: 'TopicManager',

  data() {
    return {
      dialogVisible: false,
      dialogTitle: '编辑话题',
      isEdit: false,
      activeTab: 'basic',
      form: { ...DEFAULT_FORM },
      auditReason: '',
      typeFilter: '',
      statusFilter: '',
      tagFilter: '',
      searchQuery: '',
      typeFilterOptions: [
        { label: '全部', value: '' },
        { label: '战友最痛', value: '战友最痛' },
        { label: '组织必修', value: '组织必修' },
      ],
      statusFilterOptions: [
        { label: '全部', value: '' },
        { label: '草稿', value: 'draft' },
        { label: '已发布', value: 'published' },
        { label: '已下线', value: 'offline' },
      ],
      tagFilterOptions: [
        { label: '全部', value: '' },
        { label: 'Top10', value: 'top10' },
        { label: '热门', value: 'hot' },
      ],
    }
  },

  computed: {
    ...mapState(['topics']),
    ...mapGetters(['hasPermission', 'dataScope', 'currentAdminId']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    topicList() {
      return this.topics || TOPICS
    },

    otherTopics() {
      return this.topicList.filter(t => t.id !== this.form.id)
    },

    filteredTopics() {
      let list = [...this.topicList]

      // 数据范围过滤
      if (this.dataScope === 'own') {
        list = list.filter(t => t.createdBy === this.currentAdminId)
      }

      if (this.typeFilter) {
        list = list.filter(t => t.type === this.typeFilter)
      }

      if (this.statusFilter) {
        list = list.filter(t => t.publishStatus === this.statusFilter)
      }

      if (this.tagFilter === 'top10') {
        list = list.filter(t => t.isTop10)
      } else if (this.tagFilter === 'hot') {
        list = list.filter(t => t.isHot)
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(t => t.title.toLowerCase().includes(q))
      }

      return list
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    statusLabel(status) {
      const labels = {
        draft: '草稿',
        published: '已发布',
        offline: '已下线',
      }
      return labels[status] || status
    },

    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新增话题'
      this.form = { ...DEFAULT_FORM, id: `topic-${Date.now()}` }
      this.auditReason = ''
      this.activeTab = 'basic'
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = `编辑话题 - ${row.title}`
      this.form = {
        ...DEFAULT_FORM,
        ...JSON.parse(JSON.stringify(row)),
      }
      this.auditReason = ''
      this.activeTab = 'basic'
      this.dialogVisible = true
    },

    handleDelete(row) {
      this.$confirm(`确定删除话题「${row.title}」？此操作不可恢复。`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const updated = this.topicList.filter(t => t.id !== row.id)
        this.$store.commit('SET_TOPICS', updated)

        logAction({
          action: 'DELETE',
          targetType: 'topic',
          targetId: row.id,
          reason: `删除话题: ${row.title}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('删除成功')
      }).catch(() => {})
    },

    handleSave() {
      if (!this.form.title.trim()) {
        this.$message.error('标题不能为空')
        return
      }

      const isOpsChanged = this.isEdit && this.checkOpsChanged()
      if (isOpsChanged && !this.auditReason.trim()) {
        this.$message.error('修改运营标记必须填写操作理由')
        return
      }

      const savedForm = { ...this.form }
      if (!this.isEdit) {
        savedForm.createdBy = this.currentAdminId
      }

      const updated = this.isEdit
        ? this.topicList.map(t => (t.id === this.form.id ? { ...savedForm } : t))
        : [...this.topicList, savedForm]

      this.$store.commit('SET_TOPICS', updated)

      if (this.auditReason.trim() || !this.isEdit) {
        logAction({
          action: this.isEdit ? 'UPDATE' : 'CREATE',
          targetType: 'topic',
          targetId: this.form.id,
          reason: this.auditReason || (this.isEdit ? '编辑话题' : '新建话题'),
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })
      }

      this.$message.success('保存成功')
      this.dialogVisible = false
    },

    checkOpsChanged() {
      const original = this.topicList.find(t => t.id === this.form.id)
      if (!original) return false
      return original.isTop10 !== this.form.isTop10 ||
        original.isHot !== this.form.isHot ||
        (original.sortWeight ?? 0) !== (this.form.sortWeight ?? 0)
    },

    resetForm() {
      this.form = { ...DEFAULT_FORM }
      this.auditReason = ''
    },
  },
}
</script>
